import { makeObservable, action, observable, computed } from 'mobx';
import { INCOMPLETE, COMPLETE } from '$src/components/LocationsSelector/Locations/constants';
import { controlCenterApi } from '$src/api/control-center-api';
import { Notify } from '$src/utils/notify';
import { accountStatuses, freeTrialStatus } from '$src/api/constants';
import { history } from '$src/stores/history';
import { routes } from '$src/routes';
import { generatePath } from 'react-router-dom';

export default class LocationStore {
  locations = null;
  hasError = false;
  locationId = '';
  monitoringStatus = accountStatuses.UNSUPPORTED;

  constructor(rootStore) {
    makeObservable(this, {
      locations: observable,
      locationId: observable,
      hasError: observable,
      fetchLocations: action,
      unboardedLocations: computed,
      onboardedLocations: computed,
      unmonitoringLocations: computed,
      monitoringLocations: computed,
      setLocationId: action,
      setLocations: action,
      fetchMonitoringStatus: action,
      monitoringStatus: observable,
      setOnboardingCompleted: action,
      location: computed,
      isFreeTrialEligible: computed,
      isFreeTrialActive: computed,
      isFreeTrialUnavailable: computed,
    });
    this.rootStore = rootStore;
  }

  fetchLocations = async (userId) => {
    let locationData;
    try {
      this.rootStore.sessionStore.setLoading(true);
      const locations = await controlCenterApi.fetchLocations(userId);
      if (locations?.addresses) {
        const subscriptions = await Promise.allSettled(
          /**
           * Fetch subscription data for the location using the owner's ID.
           * Note: With RBAC (Role-Based Access Control) permissions, users can access locations
           * they don't own. Therefore, we follow a two-step process:
           * 1. First, fetch all locations the user has permission to access
           * 2. Then, use the owner ID from those locations to fetch the corresponding subscription details
           * This ensures proper access control while retrieving accurate subscription information.
           */
          locations?.addresses.map((location) => location.isOwner && controlCenterApi.fetchSubscription(location.ownerId, location.locationId)),
        );
        subscriptions.forEach((subscription, index) => {
          this.updateLocationSubscription(locations.addresses[index], subscription?.value);
        });

        locations?.addresses.forEach((location) => {
          if (location.isOwner) {
            location.currentUserRole = userId !== location.ownerId ? 'Manager' : 'Owner';
          } else {
            location.currentUserRole = 'Shared User';
          }
        });
      }
      locationData = locations?.addresses || [];

      this.setLocations(locationData);
    } catch (e) {
      Notify.error('There was an error getting locations', e);
      this.setHasError(true);
    } finally {
      this.rootStore.sessionStore.setLoading(false);
    }
    return locationData;
  };

  /* eslint-disable no-param-reassign */
  updateLocationSubscription = (location, subscription) => {
    if (location) {
      location.hasEntitlements = subscription?.entitlements?.monitoring ?? false;
      location.freeTrialStatus = subscription?.freeTrialStatus;
      location.freeTrialStartedTimestamp = subscription?.freeTrialStartedTimestamp;
      location.freeTrialExpirationTimestamp = subscription?.freeTrialExpirationTimestamp;
      location.tenant = subscription?.tenant;
      location.overagesCap = subscription?.overageCap;
      location.overagesThreshold = subscription?.entitlements?.overages_threshold;
      location.overagesStartAt = subscription?.entitlements?.overages_start_at;
      location.overagesEnabled = subscription?.entitlements?.overages_enabled;
    }
  };

  get unboardedLocations() {
    return this.locations?.filter((location) => location.onboardStatus === INCOMPLETE) || [];
  }

  get onboardedLocations() {
    return this.locations?.filter((location) => location.onboardStatus === COMPLETE) || [];
  }

  get unmonitoringLocations() {
    return this.locations?.filter((location) => location.monitoring !== 'ACTIVE') || [];
  }

  get monitoringLocations() {
    return this.locations?.filter((location) => location.monitoring === 'ACTIVE') || [];
  }

  setLocationId = (locationId) => {
    if (locationId === this.locationId);
    this.locationId = locationId;
  };

  setLocations(locations) {
    this.locations = locations;
  }

  setHasError(hasError) {
    this.hasError = hasError;
  }

  fetchMonitoringStatus = async (userId) => {
    try {
      const { status } = await controlCenterApi.fetchMonitoringStatus(userId, this.locationId);
      this.monitoringStatus = status;
    } catch (e) {
      Notify.error('There was an error getting monitoring status', e);
      this.monitoringStatus = accountStatuses.UNSUPPORTED;
    }
  };

  setOnboardingCompleted = async (userId, locationId) => {
    // On onboarding complete, locations are now stale and we need to refetch
    this.setLocations([]);
    try {
      await controlCenterApi.setOnboardingCompleted(userId, locationId);
    } catch (e) {
      Notify.error('There was an error completing the onboarding', e);
    }
  };

  get location() {
    return this.locations?.find((location) => location.locationId === this.locationId);
  }

  get isFreeTrialEligible() {
    return this.locations?.some((e) => e.freeTrialStatus === freeTrialStatus.AVAILABLE);
  }

  get isFreeTrialActive() {
    return this.location?.freeTrialStatus === freeTrialStatus.ACTIVE;
  }

  get isFreeTrialUnavailable() {
    return this.location?.freeTrialStatus === freeTrialStatus.UNAVAILABLE;
  }

  // This function assumes a location exists in the list of locations that is freeTrialEligible
  redirectToFreeTrial = (locationId) => {
    const redirectLocation = this.locations.find((l) => l.freeTrialStatus === freeTrialStatus.AVAILABLE);
    history.push(generatePath(routes.freeTrial, { locationId: locationId || redirectLocation.locationId }));
  };
}
